import { useState, useCallback } from 'react';
import {
    getCalendars,
    getCalendarEventTypes,
    getCalendarEvents,
    createCalendarEvent,
    updateCalendarEvent,
    deleteCalendarEvent,
    completeEvent as completeEventService, // Import the service method
    GetCalendarEventsParams,
    createFeelingEvent,
    CreateFeelingEventDto,
    updateFeelingEvent,
    UpdateFeelingEventDto,
    createFluidEvent,
    CreateFluidEventDto,
    updateFluidEvent,
    UpdateFluidEventDto
} from '../services/calendar.service';
import { Calendar, CalendarEventDto, CalendarEventType, CreateCalendarEventDto, UpdateCalendarEventDto } from '../types/calendar';
import { User } from '../types/auth.types'; // Import User type

interface CalendarLoadingState {
    initial: boolean;
    events: boolean;
    testView: boolean;
    action: boolean;
}

export const useCalendarEvents = (user: User | null) => { // Accept user as an argument
    const [calendars, setCalendars] = useState<Calendar[]>([]);
    const [eventTypes, setEventTypes] = useState<CalendarEventType[]>([]);
    const [events, setEvents] = useState<CalendarEventDto[]>([]);
    const [testViewEvents, setTestViewEvents] = useState<CalendarEventDto[]>([]);
    
    const [loading, setLoading] = useState<CalendarLoadingState>({
        initial: false,
        events: false,
        testView: false,
        action: false,
    });
    const [error, setError] = useState<string | null>(null);

    // Function to adjust params for expert
    const adjustParamsForExpert = (params: GetCalendarEventsParams): GetCalendarEventsParams => {
        if (user?.roles.includes('expert')) {
            const appointmentTypeId = '6';
            const newParams = { ...params };
            if (!newParams.eventTypeIds) {
                newParams.eventTypeIds = appointmentTypeId;
            } else if (!newParams.eventTypeIds.split(',').includes(appointmentTypeId)) {
                newParams.eventTypeIds += `,${appointmentTypeId}`;
            }
            return newParams;
        }
        return params;
    };

    const fetchInitialData = useCallback(async () => {
        setLoading(prev => ({ ...prev, initial: true }));
        setError(null);
        try {
            const [calendarsData, eventTypesData] = await Promise.all([
                getCalendars(),
                getCalendarEventTypes()
            ]);
            setCalendars(calendarsData || []);
            setEventTypes(eventTypesData || []);
        } catch (err) {
            const msg = 'Не вдалося завантажити початкові дані.';
            setError(msg);
            console.error(msg, err);
        } finally {
            setLoading(prev => ({ ...prev, initial: false }));
        }
    }, []);

    const fetchEvents = useCallback(async (params: GetCalendarEventsParams) => {
        setLoading(prev => ({ ...prev, events: true }));
        setError(null);
        try {
            const adjustedParams = adjustParamsForExpert(params);
            const eventsData = await getCalendarEvents(adjustedParams);
            setEvents(eventsData || []);
        } catch (err) {
            const msg = 'Не вдалося завантажити події.';
            setError(msg);
            console.error(msg, err);
        } finally {
            setLoading(prev => ({ ...prev, events: false }));
        }
    }, [user]); // Add user to dependency array

    const fetchTestViewEvents = useCallback(async (params: GetCalendarEventsParams) => {
        setLoading(prev => ({ ...prev, testView: true }));
        setError(null);
        try {
            const adjustedParams = adjustParamsForExpert(params);
            const eventsData = await getCalendarEvents(adjustedParams);
            setTestViewEvents(eventsData || []);
        } catch (err) {
            const msg = 'Не вдалося завантажити тестовий список подій.';
            setError(msg);
            console.error(msg, err);
        } finally {
            setLoading(prev => ({ ...prev, testView: false }));
        }
    }, [user]); // Add user to dependency array

    const handleAction = async <T,>(action: Promise<T>, failureMessage: string): Promise<T | null> => {
        setLoading(prev => ({ ...prev, action: true }));
        setError(null);
        try {
            return await action;
        } catch (err) {
            setError(failureMessage);
            console.error(failureMessage, err);
            throw err;
        } finally {
            setLoading(prev => ({ ...prev, action: false }));
        }
    };

    const addEvent = useCallback((data: CreateCalendarEventDto) => handleAction(createCalendarEvent(data), 'Не вдалося зберегти подію.'), []);
    const addFeelingEvent = useCallback((data: CreateFeelingEventDto) => handleAction(createFeelingEvent(data), 'Не вдалося зберегти відчуття.'), []);
    const addFluidEvent = useCallback((data: CreateFluidEventDto) => handleAction(createFluidEvent(data), 'Не вдалося зберегти рідину.'), []);
    const updateFeelingEventAction = useCallback((id: number, data: UpdateFeelingEventDto) => handleAction(updateFeelingEvent(id, data), 'Не вдалося оновити відчуття.'), []);
    const updateFluidEventAction = useCallback((id: number, data: UpdateFluidEventDto) => handleAction(updateFluidEvent(id, data), 'Не вдалося оновити рідину.'), []);
    const updateEvent = useCallback((id: number, data: UpdateCalendarEventDto) => handleAction(updateCalendarEvent(id, data), 'Не вдалося оновити подію.'), []);
    const removeEvent = useCallback((id: number) => handleAction(deleteCalendarEvent(id), 'Не вдалося видалити подію.'), []);
    
    const completeEvent = useCallback(async (id: number, isCompleted: boolean) => {
        try {
            setLoading(prev => ({ ...prev, action: true }));
            setError(null);
            
            let updatedEvent: CalendarEventDto;
            
            if (isCompleted) {
                // Use the dedicated endpoint for completing
                updatedEvent = await completeEventService(id);
            } else {
                // Use the update endpoint for un-completing
                updatedEvent = await updateCalendarEvent(id, { isCompleted: false });
            }

            // Update events state locally with the full updated event object
            setEvents(prevEvents =>
                prevEvents.map(event =>
                    event.id === id ? updatedEvent : event
                )
            );
            // Update testViewEvents state locally with the full updated event object
            setTestViewEvents(prevTestViewEvents =>
                prevTestViewEvents.map(event =>
                    event.id === id ? updatedEvent : event
                )
            );
        } catch (err) {
            const msg = 'Не вдалося оновити статус події.';
            setError(msg);
            console.error(msg, err);
            throw err;
        } finally {
            setLoading(prev => ({ ...prev, action: false }));
        }
    }, []);

    return {
        calendars,
        eventTypes,
        events,
        testViewEvents,
        loading,
        error,
        setCalendars,
        setEventTypes,
        setError,
        fetchInitialData,
        fetchEvents,
        fetchTestViewEvents,
        addEvent,
        addFeelingEvent,
        addFluidEvent,
        updateEvent,
        updateFeelingEvent: updateFeelingEventAction,
        updateFluidEvent: updateFluidEventAction,
        removeEvent,
        completeEvent,
    };
};
